import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Eye, EyeOff, KeyRound, Plus, Save, Trash2, User } from 'lucide-react'
import { fetchApi, OrzForgeApiError } from '../lib/api'
import type { AccountMetadata, AccountProfile, ApiKeyItem, ApiKeyList } from '../types'

export function SettingsPage() {
  const queryClient = useQueryClient()
  const profileQuery = useQuery<AccountProfile>({
    queryKey: ['profile'],
    queryFn: async () => (await fetchApi('/v1/profile')) as AccountProfile,
  })

  const [form, setForm] = useState<AccountMetadata>({ name: '', email: '', created_at: '' })
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (profileQuery.data) {
      setForm({
        name: profileQuery.data.account.name ?? '',
        email: profileQuery.data.account.email ?? '',
        created_at: profileQuery.data.account.created_at ?? '',
      })
    }
  }, [profileQuery.data])

  const mutation = useMutation({
    mutationFn: async (metadata: AccountMetadata) => {
      return (await fetchApi('/v1/profile', {
        method: 'PATCH',
        body: JSON.stringify(metadata),
      })) as { account_id: string; metadata: AccountMetadata }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate(form)
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Ajustes</h1>
        <p className="text-sm text-gray-400">Administra los datos de tu cuenta de OrzForge.</p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-xl border border-white/10 bg-panel p-6"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10 text-brand-400">
            <User className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold">Perfil público</p>
            <p className="text-xs text-gray-500">Estos datos se almacenan en KV.</p>
          </div>
        </div>

        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-gray-300">
            Nombre
          </label>
          <input
            id="name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-300">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div>
          <label htmlFor="created_at" className="mb-1.5 block text-sm font-medium text-gray-300">
            Fecha de creación (ISO 8601)
          </label>
          <input
            id="created_at"
            type="text"
            value={form.created_at}
            onChange={(e) => setForm((f) => ({ ...f, created_at: e.target.value }))}
            className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>

        {mutation.error && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {mutation.error instanceof OrzForgeApiError
              ? mutation.error.message
              : 'Error guardando perfil'}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {mutation.isPending ? 'Guardando...' : 'Guardar cambios'}
          </button>
          {saved && <span className="text-sm text-green-400">Guardado</span>}
        </div>
      </form>

      <ApiKeysSection />

      <div className="rounded-xl border border-white/10 bg-panel p-6">
        <h2 className="mb-3 font-semibold">Seguridad</h2>
        <p className="text-sm text-gray-400">
          Tu API key se guarda únicamente en el almacenamiento local del navegador. Desde aquí
          puedes crear o revocar keys; una vez cerrada la ventana, el valor completo no se
          muestra de nuevo.
        </p>
      </div>
    </div>
  )
}

function ApiKeysSection() {
  const queryClient = useQueryClient()
  const [newKeyName, setNewKeyName] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [visibleKeyId, setVisibleKeyId] = useState<string | null>(null)

  const keysQuery = useQuery<ApiKeyList>({
    queryKey: ['api-keys'],
    queryFn: async () => (await fetchApi('/v1/keys')) as ApiKeyList,
  })

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      return (await fetchApi('/v1/keys', {
        method: 'POST',
        body: JSON.stringify({ name }),
      })) as { api_key: string; account_id: string }
    },
    onSuccess: (data) => {
      setCreatedKey(data.api_key)
      setNewKeyName('')
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      return (await fetchApi(`/v1/keys/${id}`, { method: 'DELETE' })) as { revoked: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!newKeyName.trim()) return
    createMutation.mutate(newKeyName.trim())
  }

  const keys = keysQuery.data?.keys ?? []

  return (
    <div className="rounded-xl border border-white/10 bg-panel p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10 text-brand-400">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <p className="font-semibold">API Keys</p>
          <p className="text-xs text-gray-500">Keys para usar OrzForge desde código o agentes.</p>
        </div>
      </div>

      <form onSubmit={handleCreate} className="mb-4 flex gap-2">
        <input
          type="text"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          placeholder="Nombre de la key (ej. Producción, Local)"
          className="flex-1 rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        />
        <button
          type="submit"
          disabled={createMutation.isPending || !newKeyName.trim()}
          className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
        >
          <Plus className="h-4 w-4" />
          Crear
        </button>
      </form>

      {createdKey && (
        <div className="mb-4 rounded-lg border border-green-500/20 bg-green-500/10 p-3">
          <p className="mb-1 text-sm font-medium text-green-400">API key generada</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={createdKey}
              type={visibleKeyId === 'created' ? 'text' : 'password'}
              className="flex-1 rounded border border-white/10 bg-surface px-2 py-1 text-xs text-white"
            />
            <button
              onClick={() => navigator.clipboard.writeText(createdKey)}
              className="rounded p-1.5 text-gray-400 hover:text-white"
              title="Copiar"
            >
              <Copy className="h-4 w-4" />
            </button>
            <button
              onClick={() => setVisibleKeyId((id) => (id === 'created' ? null : 'created'))}
              className="rounded p-1.5 text-gray-400 hover:text-white"
              title={visibleKeyId === 'created' ? 'Ocultar' : 'Mostrar'}
            >
              {visibleKeyId === 'created' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">Guárdala ahora. No se mostrará de nuevo.</p>
        </div>
      )}

      <div className="space-y-2">
        {keys.map((k) => (
          <ApiKeyRow
            key={k.id}
            item={k}
            visible={visibleKeyId === k.id}
            onToggle={() => setVisibleKeyId((id) => (id === k.id ? null : k.id))}
            onRevoke={() => revokeMutation.mutate(k.id)}
            revoking={revokeMutation.isPending}
          />
        ))}
        {keys.length === 0 && !keysQuery.isLoading && (
          <p className="py-4 text-center text-sm text-gray-500">No tienes API keys todavía.</p>
        )}
      </div>

      {(createMutation.error || revokeMutation.error) && (
        <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {createMutation.error instanceof OrzForgeApiError
            ? createMutation.error.message
            : revokeMutation.error instanceof OrzForgeApiError
              ? revokeMutation.error.message
              : 'Error de conexión'}
        </div>
      )}
    </div>
  )
}

function ApiKeyRow({
  item,
  visible,
  onToggle,
  onRevoke,
  revoking,
}: {
  item: ApiKeyItem
  visible: boolean
  onToggle: () => void
  onRevoke: () => void
  revoking: boolean
}) {
  const masked = `orz_${'*'.repeat(8)}${item.last_four}`
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-surface px-3 py-2">
      <div>
        <p className="text-sm font-medium text-white">{item.name || 'API Key'}</p>
        <p className="text-xs text-gray-500">{new Date(item.created_at).toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={masked}
          type={visible ? 'text' : 'password'}
          className="w-40 rounded border border-white/10 bg-panel px-2 py-1 text-xs text-white"
        />
        <button
          onClick={onToggle}
          className="rounded p-1.5 text-gray-400 hover:text-white"
          title={visible ? 'Ocultar' : 'Mostrar'}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
        <button
          onClick={onRevoke}
          disabled={revoking}
          className="rounded p-1.5 text-red-400 hover:text-red-300 disabled:opacity-60"
          title="Revocar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
